import { computed, Injectable, InjectionToken, Provider, Signal, signal } from '@angular/core';

export interface RepresentationModeState {
	active: boolean;
	representedUserId?: string;
	representedUserName?: string;
}

const STORAGE_KEY = 'lvr_representation_mode';

export interface RepresentationModeProvider {
	mode: Signal<RepresentationModeState>;
	isActive: Signal<boolean>;
	activate(representedUser?: { id?: string; name?: string }): void;
	deactivate(): void;
	clear(): void;
}

export const REPRESENTATION_MODE = new InjectionToken<RepresentationModeProvider>('REPRESENTATION_MODE');

@Injectable()
export class RepresentationModeService implements RepresentationModeProvider {
	private readonly state = signal<RepresentationModeState>({ active: false });

	public readonly mode = this.state.asReadonly();
	public readonly isActive = computed(() => this.state().active);

	public constructor() {
		const stored = this.readStoredState();
		if (stored) {
			this.state.set({
				active: Boolean(stored.active),
				representedUserId: stored.representedUserId,
				representedUserName: stored.representedUserName,
			});
		}
	}

	public activate(representedUser?: { id?: string; name?: string }) {
		this.setState({
			active: true,
			representedUserId: representedUser?.id,
			representedUserName: representedUser?.name,
		});
	}

	public deactivate() {
		this.setState({ active: false });
	}

	public clear() {
		sessionStorage.removeItem(STORAGE_KEY);
		this.state.set({ active: false });
	}

	private setState(next: RepresentationModeState) {
		this.state.set(next);
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
	}

	private readStoredState(): RepresentationModeState | undefined {
		try {
			const raw = sessionStorage.getItem(STORAGE_KEY);
			return raw ? (JSON.parse(raw) as RepresentationModeState) : undefined;
		} catch {
			return undefined;
		}
	}
}

export const provideRepresentationMode = (): Provider => ({
	provide: REPRESENTATION_MODE,
	useClass: RepresentationModeService,
});
